import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Send, Search, ChevronDown, Check, CalendarClock, CheckSquare } from "lucide-react";
import { Avatar } from "../saker/Avatar";
import { BulkBar } from "./BulkBar";
import { dagerSiden, fmtSiden, fmtKortDato, datoIdag, datoOmDager } from "./eventosUtils";

export const GRUPPER = [
  { k: "ikke_kontaktet", label: "Ikke kontaktet", dot: "#98A2B3" },
  { k: "venter", label: "Venter svar", dot: "#0EA5E9" },
  { k: "maybe", label: "Kanskje", dot: "#D97706" },
  { k: "yes", label: "Ja", dot: "#10B981" },
  { k: "no", label: "Nei", dot: "#DC2626" },
];

const KONTAKTTYPER = ["E-post sendt", "Purret", "Telefonsamtale", "Møte holdt"];

const initialer = (navn) =>
  String(navn || "?").trim().split(/\s+/).slice(0, 2).map((d) => d[0]).join("").toUpperCase();

const antKontakter = (it) => (it.activity || []).filter((a) => String(a.text || "").startsWith("Kontakt")).length;

const Popover = ({ aapen, onLukk, children, testid, bredde = 190 }) => {
  useEffect(() => {
    if (!aapen) return undefined;
    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); onLukk(); } };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [aapen, onLukk]);
  return aapen ? (
    <>
      <div className="fixed inset-0 z-30" onClick={onLukk} />
      <div className="sak-modal absolute left-0 top-full mt-1.5 z-40 bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-1.5 max-h-[300px] overflow-y-auto" style={{ width: bredde }} data-testid={testid}>
        {children}
      </div>
    </>
  ) : null;
};

const MenyKnapp = ({ onClick, children, testid, dus }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12px] text-left hover:bg-neutral-50 transition-colors ${dus ? "text-neutral-400" : "text-neutral-700"}`}
    data-testid={testid}
  >
    {children}
  </button>
);

const KontaktStatus = ({ item, gruppe }) => {
  if (gruppe === "no") {
    return <span className="text-[10.5px] font-medium text-red-500">{item.declineReason ? `Avslått · ${item.declineReason}` : "Avslått"}</span>;
  }
  if (item.followUp) {
    const over = dagerSiden(item.followUp);
    if (over > 0) return <span className="bud-mono text-[10.5px] font-semibold text-amber-600">Oppfølging {over} d over</span>;
    if (over === 0) return <span className="bud-mono text-[10.5px] font-semibold text-amber-600">Følg opp i dag</span>;
    return <span className="bud-mono text-[10.5px] text-neutral-400">Følg opp {fmtKortDato(item.followUp)}</span>;
  }
  const dager = dagerSiden(item.lastContact);
  if (dager == null) {
    return gruppe === "venter"
      ? <span className="text-[10.5px] font-medium text-amber-600">Ingen kontakt logget</span>
      : <span className="text-[10.5px] text-neutral-300">—</span>;
  }
  const purre = gruppe === "venter" && dager > 10;
  return (
    <span className={`bud-mono text-[10.5px] tabular-nums ${purre ? "font-semibold text-amber-600" : "text-neutral-400"}`}>
      {purre ? `${dager} d — på tide å purre` : fmtSiden(item.lastContact)}
    </span>
  );
};

const KontaktInfo = ({ item, gruppe }) => {
  const n = antKontakter(item);
  return (
    <div className="text-right">
      <KontaktStatus item={item} gruppe={gruppe} />
      {(n > 0 || item.ball) && (
        <p className="text-[9.5px] text-neutral-300 mt-0.5">
          {n > 0 && `${n} kontakt${n > 1 ? "er" : ""}`}
          {n > 0 && item.ball && " · "}
          {item.ball && `ball hos ${item.ball === "us" ? "oss" : "dem"}`}
        </p>
      )}
    </div>
  );
};

const StageVelger = ({ item, kompaktVisning, cfg, onPatch }) => {
  const [aapen, setAapen] = useState(false);
  const farge = cfg.stageFarge(item.stage);
  const pct = Math.round((((cfg.stageIndex[item.stage] ?? 0) + 1) / (cfg.stages.length - 1)) * 100);
  const t = cfg.testPrefix;
  return (
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setAapen((v) => !v)}
        className={kompaktVisning
          ? "inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border border-[#E7E4DB] hover:border-neutral-400 transition-colors"
          : "block w-[150px] text-left group/stage"}
        style={kompaktVisning ? { color: farge } : undefined}
        title="Bytt stadium"
        data-testid={`${t}-stage-btn-${item.id}${kompaktVisning ? "-m" : ""}`}
      >
        {kompaktVisning ? (
          <>{cfg.stageLabel[item.stage]} <ChevronDown size={10} className="opacity-50" /></>
        ) : (
          <>
            <span className="flex items-center gap-1 text-[10.5px] font-semibold" style={{ color: farge }}>
              {cfg.stageLabel[item.stage]}
              <ChevronDown size={10} className="opacity-0 group-hover/stage:opacity-60 transition-opacity" />
            </span>
            <span className="mt-1.5 block h-[3px] rounded-full bg-neutral-100 overflow-hidden">
              <span className="block h-full rounded-full transition-all" style={{ width: `${Math.min(pct, 100)}%`, background: farge }} />
            </span>
          </>
        )}
      </button>
      {aapen && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setAapen(false)} />
          <div className="sak-modal absolute right-0 top-full mt-1.5 z-40 w-[190px] max-h-[300px] overflow-y-auto bg-white border border-[#E7E4DB] rounded-xl shadow-xl p-1.5" data-testid={`${t}-stage-meny-${item.id}`}>
            {cfg.stages.map((st) => (
              <button
                key={st.k}
                type="button"
                onClick={() => { setAapen(false); if (st.k !== item.stage) onPatch(item.id, { stage: st.k }); }}
                className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[12px] text-left hover:bg-neutral-50 transition-colors"
                data-testid={`${t}-stage-valg-${st.k}-${item.id}`}
              >
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: cfg.stageFarge(st.k) }} />
                <span className={st.k === item.stage ? "font-semibold text-neutral-900" : "text-neutral-600"}>{st.label}</span>
                {st.k === item.stage && <Check size={12} className="ml-auto text-neutral-400" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const chipStil = (aktiv, farge) =>
  aktiv
    ? { background: farge, borderColor: farge, color: "#fff" }
    : { borderColor: "#E7E4DB", color: farge };

const chipKl = "text-[11px] font-semibold rounded-full px-2.5 py-1 border transition-all";

const Rad = ({ item, gruppe, index, eier, cfg, onOpen, onPatch, velgModus, valgt, onVelg }) => {
  const gr = GRUPPER.find((g) => g.k === gruppe);
  const t = cfg.testPrefix;
  const navn = cfg.navn(item);
  const [neiMeny, setNeiMeny] = useState(false);
  const [loggMeny, setLoggMeny] = useState(false);
  const [folgMeny, setFolgMeny] = useState(false);

  const ja = () => {
    if (gruppe === "yes") return;
    onPatch(item.id, { stage: cfg.jaStage, signal: null, ball: null });
    toast.success(`${navn} — flyttet til Muntlig ja`);
  };
  const kanskje = () => {
    if (item.signal === "maybe") onPatch(item.id, { signal: null });
    else onPatch(item.id, { signal: "maybe", ball: "us" });
  };
  const avslaa = (grunn) => {
    setNeiMeny(false);
    onPatch(item.id, { stage: "declined", declineReason: grunn, signal: null, ball: null });
    toast.success(`${navn} — flyttet til Avslått`);
  };
  const gjenaapne = () => {
    setNeiMeny(false);
    onPatch(item.id, { stage: cfg.gjenaapneStage, declineReason: "", signal: null });
    toast.success(`${navn} — gjenåpnet`);
  };
  const loggKontakt = (note) => {
    setLoggMeny(false);
    onPatch(item.id, { lastContact: datoIdag(), contactNote: note || "", ball: "them" });
    toast.success(note ? `${note} — logget på ${navn}` : `Kontakt logget — ${navn}`);
  };
  const settFolgOpp = (dato) => {
    setFolgMeny(false);
    onPatch(item.id, { followUp: dato || "" });
    if (dato) toast.success(`Oppfølging ${fmtKortDato(dato)} — ${navn}`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => (velgModus ? onVelg(item.id) : onOpen(item.id))}
      onKeyDown={(e) => { if (e.key === "Enter") (velgModus ? onVelg(item.id) : onOpen(item.id)); }}
      className={`sak-inn bg-white border rounded-xl px-3.5 sm:px-4 py-3 hover:shadow-sm transition-all cursor-pointer ${
        valgt ? "border-neutral-900" : "border-[#E7E4DB] hover:border-neutral-400"
      }`}
      style={{ animationDelay: `${Math.min(index, 8) * 30}ms` }}
      data-testid={`${t}-rad-${item.id}`}
    >
      <div className="flex items-center gap-3">
        {velgModus ? (
          <span
            className={`w-8 h-8 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
              valgt ? "bg-neutral-900 border-neutral-900 text-white" : "bg-white border-[#D8D4C8] text-transparent"
            }`}
            data-testid={`${t}-velg-rad-${item.id}`}
          >
            <Check size={14} />
          </span>
        ) : (
          <span
            className="w-8 h-8 rounded-full bg-[#F1EFE8] text-neutral-700 text-[11px] font-semibold flex items-center justify-center shrink-0"
            style={{ boxShadow: `0 0 0 1.5px ${gr?.dot || "#E7E4DB"}` }}
          >
            {initialer(navn)}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] font-medium text-neutral-900 truncate flex items-center gap-1.5">
            {navn}
            {item.published && <span className="w-1.5 h-1.5 rounded-full bg-[#F1E75A] border border-neutral-400 shrink-0" title="Publisert på nettsiden" />}
          </p>
          <p className="text-[11px] text-neutral-400 truncate">{cfg.undertekst(item)}</p>
        </div>
        {eier && (
          <span className="hidden sm:block shrink-0" title={`Ansvarlig: ${eier.name}`} data-testid={`${t}-eier-${item.id}`}>
            <Avatar person={eier} size={20} />
          </span>
        )}
        <div className="hidden md:block shrink-0">
          <StageVelger item={item} cfg={cfg} onPatch={onPatch} />
        </div>
        <div className="hidden sm:block w-[150px] shrink-0">
          <KontaktInfo item={item} gruppe={gruppe} />
        </div>
      </div>

      {!velgModus && (
      <div className="mt-2.5 pl-[44px] flex items-center gap-1.5 flex-wrap" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={ja} className={chipKl} style={chipStil(gruppe === "yes", "#10B981")}
          title={gruppe === "yes" ? "Allerede ja" : "Sett Muntlig ja"} data-testid={`${t}-ja-${item.id}`}>
          Ja
        </button>
        <button type="button" onClick={kanskje} className={chipKl} style={chipStil(gruppe === "maybe", "#D97706")}
          title="Sett/fjern kanskje" data-testid={`${t}-kanskje-${item.id}`}>
          Kanskje
        </button>
        <div className="relative">
          <button type="button" onClick={() => setNeiMeny((v) => !v)} className={chipKl} style={chipStil(gruppe === "no", "#DC2626")}
            data-testid={`${t}-nei-${item.id}`}>
            Nei
          </button>
          <Popover aapen={neiMeny} onLukk={() => setNeiMeny(false)} testid={`${t}-nei-meny-${item.id}`}>
            {gruppe === "no" ? (
              <MenyKnapp onClick={gjenaapne} testid={`${t}-gjenaapne-${item.id}`}>Gjenåpne — tilbake til {cfg.gjenaapneLabel}</MenyKnapp>
            ) : (
              <>
                <p className="px-2.5 pt-1 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">Avslagsgrunn</p>
                {cfg.avslagsgrunner.map((g, gi) => (
                  <MenyKnapp key={g} onClick={() => avslaa(g)} testid={`${t}-avslag-${gi}-${item.id}`}>{g}</MenyKnapp>
                ))}
              </>
            )}
          </Popover>
        </div>

        <span className="w-px h-4 bg-[#E7E4DB] mx-0.5 hidden sm:block" />

        <div className="relative">
          <button type="button" onClick={() => setLoggMeny((v) => !v)}
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-500 rounded-full px-2.5 py-1 border border-[#E7E4DB] hover:border-neutral-400 hover:text-neutral-800 transition-colors"
            data-testid={`${t}-logg-${item.id}`}>
            <Send size={10.5} /> Logg kontakt
          </button>
          <Popover aapen={loggMeny} onLukk={() => setLoggMeny(false)} testid={`${t}-logg-meny-${item.id}`} bredde={180}>
            {KONTAKTTYPER.map((k, ki) => (
              <MenyKnapp key={k} onClick={() => loggKontakt(k)} testid={`${t}-logg-type-${ki}-${item.id}`}>{k}</MenyKnapp>
            ))}
            <div className="my-1 border-t border-neutral-100" />
            <MenyKnapp dus onClick={() => loggKontakt("")} testid={`${t}-logg-uten-${item.id}`}>Bare dato — i dag</MenyKnapp>
          </Popover>
        </div>

        <div className="relative">
          <button type="button" onClick={() => setFolgMeny((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
              item.followUp ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] text-neutral-500 hover:border-neutral-400 hover:text-neutral-800"
            }`}
            data-testid={`${t}-folgopp-${item.id}`}>
            <CalendarClock size={10.5} /> {item.followUp ? fmtKortDato(item.followUp) : "Følg opp"}
          </button>
          <Popover aapen={folgMeny} onLukk={() => setFolgMeny(false)} testid={`${t}-folgopp-meny-${item.id}`} bredde={190}>
            {[{ l: "I morgen", d: 1 }, { l: "Om 3 dager", d: 3 }, { l: "Om 1 uke", d: 7 }].map((h) => (
              <MenyKnapp key={h.d} onClick={() => settFolgOpp(datoOmDager(h.d))} testid={`${t}-folgopp-valg-${h.d}-${item.id}`}>{h.l}</MenyKnapp>
            ))}
            <div className="px-2.5 py-1.5">
              <input
                type="date"
                onChange={(e) => { if (e.target.value) settFolgOpp(e.target.value); }}
                className="w-full text-[11.5px] border border-[#E7E4DB] rounded-lg px-2 py-1 bg-white focus:outline-none focus:border-neutral-400"
                data-testid={`${t}-folgopp-dato-${item.id}`}
              />
            </div>
            {item.followUp && (
              <>
                <div className="my-1 border-t border-neutral-100" />
                <MenyKnapp dus onClick={() => settFolgOpp("")} testid={`${t}-folgopp-fjern-${item.id}`}>Fjern oppfølging</MenyKnapp>
              </>
            )}
          </Popover>
        </div>

        <span className="sm:hidden ml-auto"><KontaktInfo item={item} gruppe={gruppe} /></span>
        <span className="md:hidden"><StageVelger item={item} kompaktVisning cfg={cfg} onPatch={onPatch} /></span>
      </div>
      )}
    </div>
  );
};

export default function OutreachBase({ items, members = [], onOpen, onPatch, onBulkPatch, onBulkDelete, fokus, onFjernFokus, cfg }) {
  const [filter, setFilter] = useState(null);
  const [sok, setSok] = useState("");
  const [velgModus, setVelgModus] = useState(false);
  const [valgte, setValgte] = useState(() => new Set());
  const t = cfg.testPrefix;

  const velgToggle = (id) => setValgte((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const fokusItems = useMemo(() => {
    if (fokus === "forfalt") return items.filter((it) => it.stage !== "declined" && it.followUp && (dagerSiden(it.followUp) ?? -1) >= 0);
    if (fokus === "purre") return items.filter((it) => cfg.gruppeFor(it) === "venter" && (dagerSiden(it.lastContact) ?? -1) > 10);
    return items;
  }, [items, fokus, cfg]);

  const soekte = useMemo(() => {
    const q = sok.trim().toLowerCase();
    if (!q) return fokusItems;
    return fokusItems.filter((it) => cfg.sokFelt(it).some((v) => String(v || "").toLowerCase().includes(q)));
  }, [fokusItems, sok, cfg]);

  const grupper = useMemo(() => {
    const m = Object.fromEntries(GRUPPER.map((g) => [g.k, []]));
    for (const it of soekte) m[cfg.gruppeFor(it)].push(it);
    for (const k of Object.keys(m)) {
      m[k].sort((a, b) => {
        const fa = a.followUp ? dagerSiden(a.followUp) : null;
        const fb = b.followUp ? dagerSiden(b.followUp) : null;
        if (fa !== null || fb !== null) {
          if (fa === null) return 1;
          if (fb === null) return -1;
          if (fa !== fb) return fb - fa;
        }
        const da = dagerSiden(a.lastContact) ?? 10000;
        const db = dagerSiden(b.lastContact) ?? 10000;
        if (da !== db) return db - da;
        return String(cfg.navn(a) || "").localeCompare(String(cfg.navn(b) || ""));
      });
    }
    return m;
  }, [soekte, cfg]);

  const synlige = GRUPPER.filter((g) => (filter ? g.k === filter : true));
  const antallTreff = synlige.reduce((sum, g) => sum + grupper[g.k].length, 0);
  const synligeIds = synlige.flatMap((g) => grupper[g.k].map((x) => x.id));

  const bulkStadium = async (k) => {
    const ids = [...valgte];
    await onBulkPatch(ids, { stage: k });
    toast.success(`${ids.length} flyttet til ${cfg.stageLabel[k]}`);
    setValgte(new Set());
  };
  const bulkAnsvarlig = async (mid, mnavn) => {
    const ids = [...valgte];
    await onBulkPatch(ids, { ownerId: mid });
    toast.success(mid ? `${ids.length} tildelt ${mnavn}` : `Ansvarlig fjernet på ${ids.length}`);
    setValgte(new Set());
  };
  const bulkSlett = async () => {
    const ids = [...valgte];
    if (!window.confirm(`Slette ${ids.length} fra pipelinen? Dette kan ikke angres.`)) return;
    await onBulkDelete(ids);
    setValgte(new Set());
    setVelgModus(false);
  };

  return (
    <div data-testid={`${t}-liste`}>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {fokus && (
          <button
            type="button"
            onClick={onFjernFokus}
            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold rounded-full px-3 py-1.5 bg-amber-500 text-white hover:bg-amber-600 transition-colors"
            title="Fjern fokus og vis alle"
            data-testid={`${t}-fokus-banner`}
          >
            {fokus === "forfalt" ? "Viser: oppfølginger å ta" : "Viser: venter svar over 10 dager"}
            <span className="text-white/70">✕</span>
          </button>
        )}
        <div className="relative order-last sm:order-first w-full sm:w-auto">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-300" />
          <input
            value={sok}
            onChange={(e) => setSok(e.target.value)}
            placeholder={cfg.sokPlaceholder || "Søk navn, org …"}
            className="w-full sm:w-[170px] text-[12px] border border-[#E7E4DB] rounded-full pl-8 pr-3 py-[6px] bg-white focus:outline-none focus:border-neutral-400 transition-colors placeholder:text-neutral-300"
            data-testid={`${t}-sok`}
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto sak-noscroll" data-testid={`${t}-oppsummering`}>
          {GRUPPER.map((g) => {
            const aktiv = filter === g.k;
            return (
              <button
                key={g.k}
                type="button"
                onClick={() => setFilter((cur) => (cur === g.k ? null : g.k))}
                className={`shrink-0 inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
                  aktiv ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] bg-white text-neutral-600 hover:border-neutral-400"
                }`}
                title={aktiv ? "Vis alle grupper" : `Vis kun «${g.label}»`}
                data-testid={`${t}-filter-${g.k}`}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: g.dot }} />
                {g.label}
                <span className={`bud-mono tabular-nums ${aktiv ? "text-white/60" : "text-neutral-400"}`}>{grupper[g.k].length}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => { setValgte(new Set()); setVelgModus((v) => !v); }}
          className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
            velgModus ? "border-neutral-900 bg-neutral-900 text-white" : "border-[#E7E4DB] bg-white text-neutral-600 hover:border-neutral-400"
          }`}
          title={velgModus ? "Avslutt valgmodus" : "Velg flere for å slette eller endre samlet"}
          data-testid={`${t}-velg-btn`}
        >
          <CheckSquare size={12} /> {velgModus ? "Avbryt" : "Velg"}
        </button>
      </div>

      <div className="space-y-7">
        {synlige.map((g) => {
          const liste = grupper[g.k];
          if (!liste.length) return null;
          const sumTekst = cfg.gruppeSum ? cfg.gruppeSum(liste) : null;
          return (
            <section key={g.k} data-testid={`${t}-gruppe-${g.k}`}>
              <div className="flex items-center gap-2 pb-2 mb-2.5 border-t-2 pt-2.5" style={{ borderTopColor: g.dot }}>
                <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-neutral-500">{g.label}</span>
                <span className="bud-mono text-[10.5px] tabular-nums text-neutral-400">{liste.length}</span>
                {sumTekst && <span className="bud-mono text-[10.5px] tabular-nums text-neutral-400 ml-auto" data-testid={`${t}-gruppesum-${g.k}`}>{sumTekst}</span>}
              </div>
              <div className="space-y-2">
                {liste.map((it, i) => (
                  <Rad
                    key={it.id}
                    item={it}
                    gruppe={g.k}
                    index={i}
                    eier={members.find((m) => m.id === it.ownerId) || null}
                    cfg={cfg}
                    onOpen={onOpen}
                    onPatch={onPatch}
                    velgModus={velgModus}
                    valgt={valgte.has(it.id)}
                    onVelg={velgToggle}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>

      {antallTreff === 0 && (
        <div className="text-center py-16" data-testid={`${t}-tom`}>
          <p className="text-sm font-medium text-neutral-700">{items.length === 0 ? cfg.tomTekst : "Ingen treff"}</p>
          {(sok || filter) && (
            <button
              type="button"
              onClick={() => { setSok(""); setFilter(null); }}
              className="mt-3 text-[12px] font-medium text-neutral-600 border border-[#E7E4DB] bg-white rounded-lg px-3 py-1.5 hover:border-neutral-400 transition-colors"
              data-testid={`${t}-nullstill`}
            >
              Nullstill søk og filter
            </button>
          )}
        </div>
      )}

      {velgModus && (
        <BulkBar
          antall={valgte.size}
          alleValgt={valgte.size === synligeIds.length}
          onVelgAlle={() => setValgte(valgte.size === synligeIds.length ? new Set() : new Set(synligeIds))}
          stages={cfg.stages}
          stageFarge={cfg.stageFarge}
          stageLabel={cfg.stageLabel}
          members={members}
          onStadium={bulkStadium}
          onAnsvarlig={bulkAnsvarlig}
          onSlett={bulkSlett}
          onLukk={() => { setVelgModus(false); setValgte(new Set()); }}
          t={t}
        />
      )}
    </div>
  );
}
